import { parseAccountIdsFromString } from '@/helpers/parseAccountIdsFromString';
import { createAccountIdsValidator } from '@/helpers/validateAccountIds';
import { useRunTaskMutation } from '@/services/adminTasks';
import { Box, Button, FormikTextareaInput, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { object, string, Schema } from 'yup';

import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';

import { BulkModifyAccountsResults, BulkModifyAccountsResultsData } from './BulkModifyAccountsResults';

interface AddFeocExceptionFormData {
  accountIds: string;
}

const addFeocExceptionSchema = {
  accountIds: string()
    .required('Accounts are required')
    .test('is-valid-mongo-ids', 'Invalid account ids', createAccountIdsValidator()),
};

export const AddFEOCExceptionFormSchema: Schema<AddFeocExceptionFormData> = object()
  .shape(addFeocExceptionSchema)
  .noUnknown(true);

export function AddFEOCException() {
  const [results, setResults] = useState<BulkModifyAccountsResultsData | undefined>();
  const [runTask] = useRunTaskMutation();

  const handleSubmit = async (
    values: AddFeocExceptionFormData,
    { setSubmitting }: FormikHelpers<AddFeocExceptionFormData>,
  ) => {
    const loadingToastId = toast.loading('Processing accounts...');
    try {
      let accountIds = await parseAccountIdsFromString(values.accountIds);
      accountIds = accountIds.filter((id: string) => id.trim().length > 0);

      const addFeocExceptionResults = await runTask({
        task: 'addFeocException',
        accountIds,
      }).unwrap();

      setResults(addFeocExceptionResults);
    } catch (e) {
      console.error(e);
      toast.error('Error submitting add FEOC exception request');
    } finally {
      toast.dismiss(loadingToastId);
      setSubmitting(false);
    }
  };

  const initialValues: AddFeocExceptionFormData = { accountIds: '' };

  const handleCloseResults = () => {
    setResults(undefined);
  };

  return (
    <>
      <Helmet>
        <title>Add FEOC Exception</title>
      </Helmet>
      <MainContainer>
        {!results ? (
          <Formik
            validationSchema={AddFEOCExceptionFormSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, submitForm }) => (
              <Form>
                <Box>
                  <Box>
                    <FormSection title="Accounts" description="One account ID per line, or comma separated">
                      <Field
                        name="accountIds"
                        id="accountIds"
                        component={FormikTextareaInput}
                        isDisabled={isSubmitting}
                        rows={10}
                      />
                    </FormSection>
                  </Box>
                </Box>
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-end',
                  }}
                  justifyContent={{
                    tablet: 'flex-end',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding="lg 0 0 0"
                >
                  <Button size="md" variant="primary" type="submit" onClick={submitForm} isLoading={isSubmitting}>
                    Add FEOC Exception
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <BulkModifyAccountsResults results={results} closeResults={handleCloseResults} />
        )}
      </MainContainer>
    </>
  );
}
